import type { SwappedFormOfPayment } from '@funkit/api-base'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useCallback, useState } from 'react'
import { useCheckoutBlocked } from '../geo/useCheckoutBlocked'
import { CryptoBitcoinIcon } from '../icons/CryptoBitcoinIcon'
import { PaymentMethodIcon } from '../icons/PaymentMethodIcon'
import { logger } from '../logging/logger'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import type { PaymentMethodOption } from '../screens/SelectMethodScreen'
import { WithdrawCryptoComplete } from '../screens/WithdrawCryptoComplete'
import { WithdrawCryptoSteps } from '../screens/WithdrawCryptoSteps'
import { WithdrawMethodScreen } from '../screens/WithdrawMethodScreen'
import { SwappedWithdrawScreen } from '../swapped/SwappedWithdrawScreen'
import { toWithdrawFopRows } from '../transfer/fops'
import {
  WITHDRAW_FOPS_QUERY_KEY,
  useWithdrawalFops,
} from '../transfer/hooks/useWithdrawalFops'
import type { DepositAddressOrHash } from '../withdrawal/useCryptoWithdrawalExecution'
import { useWithdrawalExecution } from '../withdrawal/useWithdrawalExecution'
import { useNativeCapabilities } from '../providers/NativeCapabilitiesContext'
import type { FlowNav, FlowScreen } from './FunkitFlowProvider'
import type {
  WithdrawalFlowCallbacks,
  WithdrawalFlowConfig,
} from './withdrawalFlowConfig'

// The synthetic id of the built-in "Crypto address" payout row. Cash rows are
// keyed by their positional index, so a non-numeric id can't collide.
const CRYPTO_WITHDRAWAL_METHOD_ID = 'crypto-withdrawal'

// Host method ids are opaque strings we don't control, and a cash row is keyed
// by its index — so a host id of `"0"` would otherwise open the first cash
// payout embed. Namespaced on the way in, stripped on the way out.
const HOST_METHOD_ID_PREFIX = 'method:'

/**
 * The live withdrawal-flow body (Swapped fiat offramp): payout-method list →
 * the Swapped sell embed → on a validated order, the on-chain leg
 * ({@link useWithdrawalExecution}) delivers the exact ordered amount to
 * Swapped's deposit address through the host wallet. Sibling of the deposit
 * FlowContent, mounted only while a withdrawal flow is open so its
 * queries run only then.
 */
export function WithdrawalFlowContent({
  checkoutId,
  config,
  callbacks,
  current,
  nav,
}: {
  checkoutId: string
  config: WithdrawalFlowConfig
  callbacks: WithdrawalFlowCallbacks
  current: FlowScreen | undefined
  nav: FlowNav
}): ReactNode {
  // Fills the recipient field on the address step. Guarded by the provider.
  const { pasteFromClipboard } = useNativeCapabilities()
  const { t } = useFunkitTranslation()
  const { isGeoblocked, isLoadingGeoCheck } = useCheckoutBlocked({
    onCheckoutBlocked: callbacks.onCheckoutBlocked,
  })
  const queryClient = useQueryClient()

  const { fops, isLoading, isBelowMinimumBalance, isError, route } =
    useWithdrawalFops({ config })

  // Methods the host resolved (see `@funkit/connect-rn/clients/*`). The picked
  // one carries the config the crypto form then runs, so nothing here knows
  // which venue — or whether there is one.
  const methodOptions = config.methods ?? []
  const [selectedMethodId, setSelectedMethodId] = useState<string>()
  const selectedMethodConfig = methodOptions.find(
    (method) => method.id === selectedMethodId,
  )?.config

  // The payout method the user picked (the whole FOP — the embed URL drives
  // the Swapped screen; type/name feed the decision-point log).
  const [selectedFop, setSelectedFop] = useState<SwappedFormOfPayment>()

  // Unrecoverable execution failure — replaces the embed with a retryable
  // error state (web parity: the iframe controller's error branch).
  const [hasExecutionError, setHasExecutionError] = useState(false)

  // Survives the form unmounting so the success screen can report on it.
  const [submittedWithdrawal, setSubmittedWithdrawal] = useState<{
    depositAddressOrHash: DepositAddressOrHash
    amountLabel: string
    usdLabel: string | undefined
    tokenLabel: string | undefined
  }>()

  // Stable so it doesn't rebuild `executeOrder` (it's in that useCallback's deps).
  const onExecutionError = useCallback(() => setHasExecutionError(true), [])
  const { executeOrder } = useWithdrawalExecution({
    config,
    route,
    callbacks,
    onError: onExecutionError,
  })

  // The embed's method list can go stale while it's open (web parity: the
  // controller invalidates on back/new-withdrawal/success).
  const invalidateWithdrawFops = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [WITHDRAW_FOPS_QUERY_KEY] })
  }, [queryClient])

  if (hasExecutionError) {
    return (
      <WithdrawMethodScreen
        title={config.title ?? t('withdraw.title')}
        methods={[]}
        hasError
        onRetry={() => {
          setHasExecutionError(false)
          invalidateWithdrawFops()
          // Reset to the method list — `back()` would close the whole sheet if
          // the user had already popped the embed before execution failed.
          nav.reset('withdrawMethod')
        }}
        onSelectMethod={() => undefined}
      />
    )
  }

  switch (current) {
    case 'withdrawMethod': {
      // Id by array index, not `<fopType>:<name>` — Swapped can return two
      // methods that share both yet carry distinct `embeddedFlowUrl`s, so a
      // key-based lookup could open the wrong payout embed. Rows are positional
      // 1:1 with `fops`, so the index resolves the exact selected FOP.
      const cashMethods: PaymentMethodOption[] = toWithdrawFopRows(t, fops).map(
        (row, i) => ({
          id: String(i),
          title: row.name,
          subtitle: row.subtitle,
          icon: <PaymentMethodIcon fop={fops[i]} />,
        }),
      )
      // Crypto is unconditional — no provider, no region gate (web geoblocks
      // nothing here either). Cash rows stay while they may still arrive; their
      // absence gets a note, not a takeover (web: showFopsList/NoCashMethodsNote).
      const showCashList = isLoading || cashMethods.length > 0
      const genericCryptoRow: PaymentMethodOption = {
        id: CRYPTO_WITHDRAWAL_METHOD_ID,
        title: t('withdrawCrypto.methodTitle'),
        subtitle: t('withdrawCrypto.methodSubtitle'),
        icon: <CryptoBitcoinIcon />,
      }
      // Host-supplied methods replace the generic crypto row: a venue whose
      // balance lives off-chain has nothing for a plain send-to-address form to
      // send. With none resolved the generic row stays as the floor, so the
      // user is never left unable to withdraw crypto at all.
      const cryptoMethods: PaymentMethodOption[] = methodOptions.length
        ? methodOptions.map((method) => ({
            id: `${HOST_METHOD_ID_PREFIX}${method.id}`,
            title: method.title,
            subtitle: method.subtitle,
            icon: method.icon ?? <CryptoBitcoinIcon />,
          }))
        : [genericCryptoRow]
      const methods: PaymentMethodOption[] = [
        ...cryptoMethods,
        // Below Swapped's minimum sell, cash stays visible but unselectable —
        // dimming explains more than hiding would.
        ...(showCashList
          ? cashMethods.map((row) => ({
              ...row,
              disabled: isBelowMinimumBalance,
            }))
          : []),
      ]
      // A failed or never-run fetch is an error, not a statement about the
      // user's region (web: `hasFopsResult`).
      const cashNote = showCashList
        ? undefined
        : isError
          ? t('withdraw.cashMethodsError')
          : t('withdraw.noCashMethods')
      return (
        <WithdrawMethodScreen
          title={config.title ?? t('withdraw.title')}
          methods={methods}
          // Deliberately NOT gated on the cash fetch: sending on-chain needs
          // nothing from it, so a skeleton over the whole list would hide a
          // ready option. Cash rows append when they arrive, as they do on web.
          loading={isLoadingGeoCheck}
          note={cashNote}
          onSelectMethod={(id) => {
            const hostMethodId = id.startsWith(HOST_METHOD_ID_PREFIX)
              ? id.slice(HOST_METHOD_ID_PREFIX.length)
              : undefined
            if (id === CRYPTO_WITHDRAWAL_METHOD_ID || hostMethodId) {
              // Rail decision point — which balance and which execution path.
              logger.info('connect-rn:crypto-withdrawal:method_selected', {
                checkoutId,
                methodId: hostMethodId ?? id,
              })
              // Undefined for the generic row, which runs the host's own config.
              setSelectedMethodId(hostMethodId)
              nav.navigate('withdrawAmount')
              return
            }
            const fop = fops[Number(id)]
            if (!fop?.embeddedFlowUrl) {
              return
            }
            // Payout-provider decision point.
            logger.info('connect-rn:withdrawal:method_selected', {
              checkoutId,
              fopType: fop.fopType,
              fopName: fop.name,
            })
            setSelectedFop(fop)
            nav.navigate('swapped')
          }}
        />
      )
    }
    case 'withdrawAmount':
    case 'withdrawAddress':
    case 'withdrawReceiveToken':
      return (
        <WithdrawCryptoSteps
          config={selectedMethodConfig ?? config}
          callbacks={callbacks}
          current={current}
          nav={nav}
          pasteFromClipboard={pasteFromClipboard}
          // Off the same config the steps run, so a host that sets a fee without
          // supplying methods still has it charged.
          quoteInputFeeTokens={
            (selectedMethodConfig ?? config).quoteInputFeeTokens ?? 0
          }
          quoteInputFeeSettled={
            (selectedMethodConfig ?? config).quoteInputFeeSettled ?? true
          }
          extraSourceGasUsd={
            (selectedMethodConfig ?? config).extraSourceGasUsd ?? 0
          }
          onSubmitted={(result) => {
            setSubmittedWithdrawal(result)
            // Replace, not push: the funds have moved, so going "back" into a
            // form that would submit again is the wrong affordance.
            nav.reset('withdrawComplete')
          }}
        />
      )
    case 'withdrawComplete':
      return submittedWithdrawal ? (
        <WithdrawCryptoComplete
          depositAddressOrHash={submittedWithdrawal.depositAddressOrHash}
          amountLabel={submittedWithdrawal.amountLabel}
          usdLabel={submittedWithdrawal.usdLabel}
          tokenLabel={submittedWithdrawal.tokenLabel}
          callbacks={callbacks}
          onDone={nav.closeFlow}
        />
      ) : null
    case 'swapped':
      return selectedFop?.embeddedFlowUrl ? (
        <SwappedWithdrawScreen
          url={selectedFop.embeddedFlowUrl}
          targetChainId={route.targetChainId}
          onBack={() => {
            invalidateWithdrawFops()
            nav.back()
          }}
          onClose={() => {
            invalidateWithdrawFops()
            nav.closeFlow()
          }}
          onNewWithdrawal={() => {
            invalidateWithdrawFops()
            nav.back()
          }}
          onWithdrawalSuccess={(data) => {
            invalidateWithdrawFops()
            callbacks.onWithdrawalSuccess?.(data)
          }}
          onOrderData={(order) => {
            // The host notification is informational — a throw from it must not
            // skip the on-chain execution (the critical leg for this order).
            try {
              callbacks.onWithdrawalOrder?.(order)
            } catch (error) {
              logger.warn('connect-rn:withdrawal:order_callback_threw', {
                order_id: order.order_id,
                error: error instanceof Error ? error.message : String(error),
              })
            }
            void executeOrder(order)
          }}
        />
      ) : null
    default:
      return null
  }
}
